import { jsx as _jsx } from "react/jsx-runtime";
import { Button } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Loader2, Mic, Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MAX_RECORDING_SECONDS, useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { apiErrorMessage } from '../lib/api/apiErrorMessage';
import { agentClient } from '../lib/connect/clients';
import { cn } from '../lib/utils';
/**
 * Dictation for the chat composer: record, transcribe through shanks, hand
 * the words back for the user to review before they become a turn.
 */ export function VoiceInputButton({ onTranscript, disabled, dense }) {
    const [isTranscribing, setIsTranscribing] = useState(false);
    const recorder = useVoiceRecorder(async ({ audio, filename })=>{
        setIsTranscribing(true);
        try {
            const { text } = await agentClient.transcribeAudio({
                audio: new Uint8Array(await audio.arrayBuffer()),
                audioFilename: filename
            });
            onTranscript(text);
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Could not transcribe that. Try again or type it.'));
        } finally{
            setIsTranscribing(false);
        }
    });
    // The recorder reports a blocked mic as state, not a throw; a composer has
    // no status line, so it surfaces as a toast. start() clears it first, so a
    // second refusal toasts again.
    useEffect(()=>{
        if (recorder.error) toast.error(recorder.error);
    }, [
        recorder.error
    ]);
    const label = recorder.isRecording ? `Stop dictation (${recorder.elapsed}s of ${MAX_RECORDING_SECONDS}s)` : isTranscribing ? 'Transcribing…' : 'Dictate a message';
    const iconClass = dense ? 'h-[13px] w-[13px]' : 'h-4 w-4';
    return /*#__PURE__*/ _jsx(Button, {
        type: "button",
        variant: "ghost",
        size: "sm",
        onClick: recorder.isRecording ? recorder.stop : recorder.start,
        disabled: disabled && !recorder.isRecording || isTranscribing,
        title: label,
        "aria-label": label,
        "aria-pressed": recorder.isRecording,
        "data-testid": Testids.assistant.voiceInputButton,
        className: cn('flex-shrink-0 h-auto font-medium', dense ? 'p-1.5' : 'p-2', recorder.isRecording ? 'text-destructive hover:text-destructive animate-pulse' : 'text-muted-foreground hover:text-foreground'),
        children: isTranscribing ? /*#__PURE__*/ _jsx(Loader2, {
            className: cn(iconClass, 'animate-spin')
        }) : recorder.isRecording ? /*#__PURE__*/ _jsx(Square, {
            className: cn(iconClass, 'fill-current')
        }) : /*#__PURE__*/ _jsx(Mic, {
            className: iconClass
        })
    });
}
